import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import type { ReportType } from '../../../../../../generated/graphql-manager';

import {
  DownloadIcon,
  IconButton,
  Paragraph,
  PlusIcon,
  Spinner,
  Tooltip,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { InfoTooltip } from 'src/components/info-tooltip';

import {
  ReportStatus,
  useGenerateReportMutation,
  useGetWebcastReportQuery,
} from '../../../../../../generated/graphql-manager';
import { useUserPermissions } from '../../../../../hooks/user-permissions';

import styles from './download-report.module.css';

export type DownloadReportProps = {
  webcastId: string;
  label: string;
  generateLabel: string;
  infoText: string;
  reportType: ReportType;
} & ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement>;

export function DownloadReport({
  webcastId,
  label,
  generateLabel,
  reportType,
  infoText,
  ...props
}: DownloadReportProps) {
  const { t } = useTranslation();
  const { isEventReportingAllowed } = useUserPermissions();

  const { data, loading, refetch } = useGetWebcastReportQuery({
    variables: { webcastId, reportType },
    skip: !webcastId || !reportType,
  });

  const report = data?.report;
  const downloadLink = report?.downloadLink || undefined;
  const isReportPending = report?.status === ReportStatus.SCHEDULED || report?.status === ReportStatus.CREATED;
  const isReportStatusNoData = report?.status === ReportStatus.NO_DATA;

  const [generateReportMutation, { loading: generating }] = useGenerateReportMutation();

  const generateReport = () => {
    if (downloadLink) return;

    generateReportMutation({
      variables: {
        input: {
          webcastId,
          type: reportType,
        },
      },
      onCompleted: (data) => {
        if (data?.generateReport?.__typename === 'GenerateReportSuccess') {
          refetch();
        }
      },
    });
  };

  useEffect(() => {
    if (isReportPending) {
      const interval = setInterval(() => {
        refetch();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isReportPending, refetch]);

  const getReportStatusMessage = (statusReport?: ReportStatus) => {
    switch (statusReport) {
      case undefined:
      case ReportStatus.EXPIRED:
      case ReportStatus.FAILED:
        return t('manager.eventSummary.reports.tooltip.generateReport');
      case ReportStatus.CREATED:
      case ReportStatus.SCHEDULED:
        return t('manager.eventSummary.reports.tooltip.scheduledReport');
      case ReportStatus.NO_DATA:
        return t('manager.eventSummary.reports.tooltip.noData');
      case ReportStatus.READY:
        return t('manager.eventSummary.reports.tooltip.downloadReport');
      default:
        return undefined;
    }
  };

  const tooltipLabel = isEventReportingAllowed
    ? getReportStatusMessage(report?.status)
    : t('manager.webcastSetup.notEditableMessage.role');

  const isDownloadButtonDisable =
    loading || generating || !isEventReportingAllowed || isReportStatusNoData || isReportPending;

  const isGenerating = loading || generating || isReportPending;

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelWrapper}>
        <Paragraph data-testid="download-report-button-label">{downloadLink ? label : generateLabel}</Paragraph>
        <InfoTooltip label={infoText} />
      </div>

      <Tooltip label={tooltipLabel}>
        <IconButton
          component={NavLink}
          to={downloadLink}
          target="_blank"
          disabled={isDownloadButtonDisable}
          onClick={generateReport}
          data-testid="download-report-button"
          aria-label={downloadLink ? label : generateLabel}
          className={styles.downloadButton}
          {...props}
        >
          {isGenerating ? (
            <Spinner size={18} style={{ margin: 'auto' }} />
          ) : downloadLink ? (
            <DownloadIcon />
          ) : (
            <PlusIcon />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
}
